import {
  EntityType,
  ProgramSummaryExportableType,
  ESAReportAttributes,
  ESAReportEntry,
  FileType,
  ScreenshotOpts,
} from 'spw-redux/modules/reports/types';
import {
  AlternativeWorkpointsBreakdown,
  ISpacesBreakDown,
} from 'spw-redux/modules/program-summary/types';
import { ProgramSummaryExportableTypes } from 'spw-redux/modules/reports/constants';
import {
  ALTERNATIVE_WORKPOINTS_CARDS,
  PROGRAM_SUMMARY_CARD_ACTIONS,
  PROGRAM_SUMMARY_CARD_WIDTH,
  PROGRAM_SUMMARY_CARDS,
  PROJECT_EXPORTABLE,
} from 'spw-esa-projects/constants';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import { categoryCardScreenshotOptions } from 'spw-projects/reports/utils';
import { SpaceTypeColor } from 'spw-redux/modules/space_types/types';
import { Report, ReportRequest } from '../Report';

export default class ProgramSummaryReport implements Report {
  exportableType: ProgramSummaryExportableType;

  projectId: number;

  entityId: number;

  entityType: EntityType;

  buildingIds: number[];

  floorIds: number[];

  breakdown: ISpacesBreakDown;

  alternativeWorkpointsBreakdown: AlternativeWorkpointsBreakdown;

  showRsf: boolean;

  shouldShowClientFacingNames: boolean;

  format: FileType;

  currentSpaceTypeColors?: { [index: string]: SpaceTypeColor };

  currentProjectColors?: { subcategories: Record<string, string> };

  constructor(attributes: ESAReportAttributes) {
    this.exportableType = attributes.exportableType as ProgramSummaryExportableType;
    this.entityId = attributes.entityId;
    this.entityType = attributes.entityType;
    this.showRsf = attributes.showRsf;
    this.shouldShowClientFacingNames = attributes.shouldShowClientFacingNames;
    this.format = attributes.format;
    this.breakdown = attributes.breakdown;
    this.alternativeWorkpointsBreakdown =
      attributes.alternativeWorkpointsBreakdown;
    this.projectId = attributes.project.id;
    this.floorIds = attributes.floorIds;
    this.buildingIds = attributes.buildingIds;
    this.currentSpaceTypeColors = attributes.currentSpaceTypeColors;
    this.currentProjectColors = attributes.currentProjectColors;
  }

  getReportRequest() {
    const reportEntry = {
      entityId: this.entityId,
      entityType: this.entityType,
      exportable: PROJECT_EXPORTABLE.PROGRAM_SUMMARY,
      variants: {
        showRsf: this.showRsf,
        shouldShowClientFacingNames: this.shouldShowClientFacingNames,
        floorIds: this.floorIds.join(','),
        buildingIds: this.buildingIds.join(','),
      },
    };

    const localStorageItems = [
      {
        name: 'spaceTypeColors',
        value: JSON.stringify(this.currentSpaceTypeColors),
      },
      {
        name: 'currentProjectColors',
        value: JSON.stringify(this.currentProjectColors),
      },
    ];

    switch (this.exportableType) {
      case ProgramSummaryExportableTypes.ONE_PAGER:
        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries: [
            {
              ...reportEntry,
              screenshotsOpts: [
                {
                  format: this.format.toUpperCase(),
                  width: 1440,
                  localStorageItems,
                },
              ],
            },
          ] as ESAReportEntry[],
        };
      case ProgramSummaryExportableTypes.ALL_CARDS_INDIVIDUALLY: {
        const categories = Object.keys(this.breakdown).filter(
          (category) =>
            this.breakdown[category].length &&
            category !== INDIVIDUAL_WORKSPACES_CATEGORY
        );

        const categoryScreenshotOptions = ['usfMix', 'seatMix']
          .map((cardType) =>
            categoryCardScreenshotOptions(
              this.format.toUpperCase(),
              INDIVIDUAL_WORKSPACES_CATEGORY,
              cardType,
              PROGRAM_SUMMARY_CARD_WIDTH[cardType]
            )
          )
          .concat(
            categories.reduce((opts, category) => {
              const categoryOpts = ['usfMix', 'ratios'].map((cardType) =>
                categoryCardScreenshotOptions(
                  this.format.toUpperCase(),
                  category,
                  cardType,
                  PROGRAM_SUMMARY_CARD_WIDTH[cardType]
                )
              );

              return opts.concat(categoryOpts);
            }, [])
          );

        let programSummaryCards: string[] = Object.values(
          PROGRAM_SUMMARY_CARDS
        );

        if (this.alternativeWorkpointsBreakdown?.all?.count > 0) {
          programSummaryCards = programSummaryCards.concat(
            Object.values(ALTERNATIVE_WORKPOINTS_CARDS)
          );
        }

        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries: [
            {
              ...reportEntry,
              screenshotsOpts: programSummaryCards
                .map(
                  (programSummaryCard: string) =>
                    ({
                      format: this.format.toUpperCase(),
                      exportable: programSummaryCard,
                      width: 1440,
                      clip: {
                        x: 0,
                        y: 0,
                        width: PROGRAM_SUMMARY_CARD_WIDTH[programSummaryCard],
                      },
                      localStorageItems,
                      pageActions:
                        PROGRAM_SUMMARY_CARD_ACTIONS[programSummaryCard],
                    } as ScreenshotOpts)
                )
                .concat(categoryScreenshotOptions),
            },
          ] as ESAReportEntry[],
        };
      }
      case ProgramSummaryExportableTypes.SUMMARY_DATA: {
        let programSummaryCards: string[] = Object.values(
          PROGRAM_SUMMARY_CARDS
        );

        if (this.alternativeWorkpointsBreakdown?.all?.count > 0) {
          programSummaryCards = programSummaryCards.concat(
            Object.values(ALTERNATIVE_WORKPOINTS_CARDS)
          );
        }

        programSummaryCards.push('programSummaryReport');

        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries: [
            {
              ...reportEntry,
              screenshotsOpts: programSummaryCards.map(
                (programSummaryCard: string) =>
                  ({
                    format: this.format.toUpperCase(),
                    exportable: programSummaryCard,
                    width: 1440,
                    clip: {
                      x: 0,
                      y: 0,
                      width: PROGRAM_SUMMARY_CARD_WIDTH[programSummaryCard],
                    },
                    localStorageItems,
                    pageActions:
                      PROGRAM_SUMMARY_CARD_ACTIONS[programSummaryCard],
                  } as ScreenshotOpts)
              ),
            },
          ],
        };
      }
      case `${INDIVIDUAL_WORKSPACES_CATEGORY}Cards`: {
        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries: [
            {
              ...reportEntry,
              screenshotsOpts: ['usfMix', 'seatMix', 'usfAndSeatMix'].map(
                (cardType) => ({
                  ...categoryCardScreenshotOptions(
                    this.format.toUpperCase(),
                    INDIVIDUAL_WORKSPACES_CATEGORY,
                    cardType,
                    PROGRAM_SUMMARY_CARD_WIDTH[cardType]
                  ),
                  localStorageItems,
                })
              ),
            },
          ],
        };
      }
      case `${MEETING_SPACES_CATEGORY}Cards`:
      case `${AMENITY_SPACES_CATEGORY}Cards`:
      case `${SUPPORT_SPACES_CATEGORY}Cards`: {
        const category = this.exportableType.replace('Cards', '');

        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries: [
            {
              ...reportEntry,
              screenshotsOpts: ['usfMix', 'ratios', 'usfMixAndRatios'].map(
                (cardType) => ({
                  ...categoryCardScreenshotOptions(
                    this.format.toUpperCase(),
                    category,
                    cardType,
                    PROGRAM_SUMMARY_CARD_WIDTH[cardType]
                  ),
                  localStorageItems,
                })
              ),
            },
          ],
        };
      }
      default:
        return {
          projectId: this.projectId,
          reportEntries: [],
        } as ReportRequest;
    }
  }
}
